// Material
import { Box, CircularProgress } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import { createStyles, makeStyles } from '@material-ui/core/styles';
// Redux
import { connect, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { setStep } from '../../redux/scholarship.actions';
import { selectIsSavingApplication, selectStep, selectSteps, selectStepsCompleted } from '../../redux/scholarship.selectors';

const useStyles = makeStyles((theme) =>
  createStyles({
    root: {
      width: '100%',
      textAlign: 'center'
    },
    bar: {
      backgroundColor: '#fafafa'
    },
    button: {
      marginRight: theme.spacing(1),
      height: '45px'
    },
    completed: {
      display: 'inline-block'
    },
    instructions: {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1)
    }
  })
);

interface IStepItem {
  id: string;
  label: string;
  num: number;
}

interface IStepperButtonsProps {
  id: string;
  afterNext: (stepLabel: string, newStep: number, newCompleted: Record<number, boolean>) => void;
  appSubmitted: boolean;
  dispatchSetStep: (oldStep: number | undefined, newStep: number) => void;
}

const HorizontalNonLinearStepperButtons = ({ id, afterNext, appSubmitted, dispatchSetStep }: IStepperButtonsProps) => {
  const classes = useStyles();
  const navigate = useNavigate();
  let isSaving = useSelector<unknown, boolean>(selectIsSavingApplication as (state: unknown) => boolean);
  const steps = useSelector<unknown, IStepItem[]>(selectSteps as (state: unknown) => IStepItem[]);
  const currentStep = useSelector<unknown, number>(selectStep as (state: unknown) => number);
  const completed = useSelector<unknown, Record<number, boolean>>(selectStepsCompleted as (state: unknown) => Record<number, boolean>);

  // Do not allow saving on a submitted application. Only disable the button if appSubmitted is true.
  if (appSubmitted) {
    isSaving = appSubmitted;
  }

  const totalSteps = () => {
    return steps.length;
  };

  const completedSteps = () => {
    return Object.keys(completed).length;
  };

  const isLastStep = () => {
    return currentStep === totalSteps() - 1;
  };

  const allStepsCompleted = () => {
    return completedSteps() === totalSteps();
  };

  const handleNext = () => {
    let stepLabel = steps[currentStep].id;
    const nextStep =
      isLastStep() && !allStepsCompleted()
        ? // It's the last step, but not all steps have been completed,
          // find the first step that has been completed
          steps.findIndex((s, i) => !(i in completed))
        : Number(currentStep) + 1;
    const newCompleted = { ...completed, [currentStep]: true };

    if (stepLabel === 'transcript') {
      delete newCompleted[currentStep];
    }

    afterNext(stepLabel, nextStep, newCompleted);
  };

  const handleBack = () => {
    if (currentStep === 0) {
      navigate('/applications');
    } else {
      const newStep = currentStep > 0 ? currentStep - 1 : 0;
      dispatchSetStep(currentStep, newStep);
      navigate(`/applications/${id}/${steps[newStep].id}`);
    }
  };

  const handleReset = () => {
    dispatchSetStep(undefined, 0);
    navigate('/applications');
    // setCompleted({});
  };

  return (
    <div className={classes.root}>
      {allStepsCompleted() ? (
        <div>
          <Typography className={classes.instructions}>All steps completed - you&apos;re finished</Typography>
          <Button onClick={handleReset}>Reset</Button>
        </div>
      ) : (
        <div>
          <div>
            <Button onClick={handleBack} className={classes.button}>
              Back
            </Button>
            <Button variant="contained" color="primary" onClick={handleNext} className={classes.button} disabled={isSaving}>
              Save and Continue
              {isSaving && (
                <Box ml={2} mt={1}>
                  <CircularProgress size={18} color={'inherit'} />
                </Box>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const mapDispatchToProps = (dispatch) => ({
  dispatchSetStep: (oldStep, newStep) => dispatch(setStep(oldStep, newStep))
});

export default connect(null, mapDispatchToProps)(HorizontalNonLinearStepperButtons);
